import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { fetchInventory } from "../../../../store/remediation/remediationSlice";

const PRETTY_LABELS = {
  kms_keys: "KMS key",
  s3_buckets: "S3 bucket",
  iam_roles: "IAM role",
  sns_topics: "SNS topic",
  vpcs: "VPC",
};

/**
 * Generic Autocomplete picker backed by GET /inventory/{type}.
 *
 * Lazily fetches the first page on mount, exposes a "Load more" button
 * when next_token is non-null. Freesolo allows the user to paste an ARN
 * the inventory hasn't returned yet (handy edge case).
 */
const ResourcePicker = ({
  inventoryType,
  accountNumber,
  value,
  onChange,
  required = false,
  label,
  description,
  disabled = false,
  error,
}) => {
  const dispatch = useDispatch();
  const cache = useSelector((s) => s.remediation.inventoryByType[inventoryType]);
  const items = cache?.items || [];
  const loading = cache?.status === "loading";
  const nextToken = cache?.next_token;
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (opened && !cache && accountNumber) {
      dispatch(
        fetchInventory({
          invType: inventoryType,
          account_number: String(accountNumber).trim(),
          next_token: null,
          page_size: 50,
        })
      );
    }
  }, [dispatch, inventoryType, accountNumber, opened, cache]);

  const options = useMemo(() => items.map((i) => i.value), [items]);
  const labelMap = useMemo(() => {
    const m = {};
    for (const i of items) m[i.value] = i.label;
    return m;
  }, [items]);

  const pretty = PRETTY_LABELS[inventoryType] || inventoryType;

  return (
    <Box sx={{ mb: 2 }}>
      <Autocomplete
        size="small"
        freeSolo
        disabled={disabled}
        options={options}
        value={value || ""}
        onChange={(_e, v) => onChange(v || "")}
        onInputChange={(_e, v, reason) => {
          if (reason === "input") onChange(v);
        }}
        onOpen={() => setOpened(true)}
        loading={loading}
        getOptionLabel={(o) => labelMap[o] || o || ""}
        renderOption={(props, opt) => (
          <li {...props}>
            <Box>
              <Typography variant="body2">{labelMap[opt] || opt}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {opt}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${label || pretty}${required ? " *" : ""}`}
            error={Boolean(error)}
            helperText={error || description}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {nextToken && (
        <Button
          size="small"
          onClick={() =>
            dispatch(
              fetchInventory({
                invType: inventoryType,
                account_number: String(accountNumber).trim(),
                next_token: nextToken,
                page_size: 50,
              })
            )
          }
          disabled={!accountNumber}
          sx={{ mt: 0.5 }}
        >
          Load more {pretty}s
        </Button>
      )}
    </Box>
  );
};

export default ResourcePicker;
